import React from 'react';

import { JsonLD, withConfig, withGridConfig } from '../common/Block.jsx';
import { EmptyContentPlaceholder } from '../layout/Builder.jsx';
import { Column, Row, Spaced } from '../common/Container.jsx';
import Defaults from '../layout/Defaults.jsx';
import { default as Image, ImageSrcSet } from '../common/Image.jsx';
import Kit from '../layout/Kit.jsx';
import Layout from '../common/Layout.jsx';
import { ContentContainer as _ContentContainer, withPaging } from '../common/List.jsx';
import { Footer, View as LoadingView } from '../common/Loading.jsx';
import Name from '../common/Name.jsx';
import Parallax from '../common/Parallax.jsx';
import ScreenSize from '../common/ScreenSize.jsx';
import * as ShowListCell from './ShowListCell.jsx';
import { withSlideshowConfiguration, withSlideshowInitialization } from '../common/Slideshow.jsx';
import Url from '../common/Url.jsx';
import Utilities from '../common/Utilities.jsx';

const defaultColumnCount = 5;

class _ShowsListComponent extends React.Component {
    constructor(props) {
        super(props);

        this.state = {};

        this.handleRequestData = this.handleRequestData.bind(this);
        this.handleResponseDataPostProcess = this.handleResponseDataPostProcess.bind(this);
    }

    componentDidMount() {
        this.props.onRegisterRequestDataHandler(this.handleRequestData);
        this.props.onRegisterResponseDataPostProcessHandler(this.handleResponseDataPostProcess);

        if (!this.props.data) {
            this.props.onLoad();
        }
    }

    componentDidUpdate(prevProps) {
        if (this.props.screenSize != this.state.screenSize) {
            this.setState({
                screenSize: this.props.screenSize
            });
        }
        if (!this.props.builder || !this.props.blockConfig || !prevProps.context || !this.props.context) {
            return;
        }
        const blockConfig = this.props.onNormalizeBlockConfig(this.props);
        if (prevProps.context.maxLimit != blockConfig.maxLimit ||
            prevProps.context.tags != blockConfig.tags ||
            prevProps.context.status != this.props.context.status) {
            this.props.onReset(this.props.onLoad);
        }
    }

    handleRequestData(requestData) {
        const blockConfig = this.props.onNormalizeBlockConfig(this.props);
        requestData.artistId = this.props.context.artistId;
        requestData.maxLimit = blockConfig.maxLimit;
        requestData.status = this.props.context.status;
        requestData.tags = blockConfig.tags;

        if (this.props.builder) {
            requestData.limit = 3;

            let columnCount = parseInt(blockConfig.columns, 10);
            if (!columnCount || isNaN(columnCount)) {
                columnCount = defaultColumnCount;
            }
            if (columnCount > 1) {
                requestData.limit = 2 * columnCount
            }
            if (requestData.maxLimit < requestData.limit) {
                requestData.limit = requestData.maxLimit
            }
        }
    }

    handleResponseDataPostProcess(data) {
        const blockConfig = this.props.onNormalizeBlockConfig(this.props);
        if (blockConfig.maxLimit && data.shows && data.shows.length >= blockConfig.maxLimit) {
            data.hasMore = false;
        }
    }

    render() {
        if (this.props.data && (!this.props.data.shows || this.props.data.shows.length == 0)) {
            if (this.props.builder) {
                let blankState;
                if (this.props.onBlankState) {
                    blankState = this.props.onBlankState(Name.Block.Show.List.Grid);
                }
                if (blankState) {
                    return blankState;
                }
                const containerStyle = this.props.containerStyle || {};
                containerStyle.width = '100%';
                return (
                    <ContentContainer>
                        <EmptyContentPlaceholder containerStyle={containerStyle} style={this.props.style}>You haven't added any exhibitions yet. Once you do, you'll see them here.</EmptyContentPlaceholder>
                    </ContentContainer>
                );
            }
            return null;
        }
        const props = this.props;
        let useCellKit;
        if (props.blockConfig && props.blockConfig.block) {
            useCellKit = props.blockConfig.block.useCellKit;
        }
        const context = props.context || {};
        const isDataLoaded = props.data && props.data.shows;
        let content;
        switch (context.displayType) {
            case 'parallax':
                if (isDataLoaded) {
                    if (props.useTokenization || useCellKit != undefined) {
                        content = (
                            <Parallax.Container.Component
                                {...props}
                                contentConfig={Defaults.cellDesign(props.config, props.blockConfig, Kit.Context.Cell.Exhibition.Parallax)}
                                data={props.data.shows}
                                slideComponent={ShowListCell.ShowCellContainerParallax} />
                        );
                    } else {
                        const data = props.data.shows.map((d) => {
                            const link = Url.getLink('', `show/${d.showId}`, { sub: props.querySub, layout: props.queryLayout, dataSub: props.queryDataSub }, props.builder);
                            return {
                                button: {
                                    link: link,
                                    text: 'View Exhibition'
                                },
                                content: {
                                    header: d.title,
                                    link: link,
                                    subheader: d.date
                                },
                                image: d.image
                            }
                        })
                        content = <Parallax.Container.Component builder={props.builder} data={data} />
                    }
                } else {
                    content = <LoadingView />
                }
                break;
            case 'slideshow':
                if (isDataLoaded) {
                    content = (
                        <Slideshow
                            {...props}
                            hasMore={props.data.hasMore}
                            listData={props.data.shows}
                            navEnabled={true}
                            responsive={true}
                            style={{}}
                            visible={true} />
                    )
                } else {
                    content = <LoadingView />
                }
                break;
            default:
                let className;
                if (isDataLoaded) {
                    if (props.useTokenization || useCellKit != undefined) {
                        const animation = props.blockConfig.block && props.blockConfig.block.animation;
                        let columnCount = props.columnCount || defaultColumnCount;
                        if (props.blockConfig.block) {
                            switch (this.state.screenSize) {
                                case ScreenSize.Mobile:
                                    columnCount = props.blockConfig.block.columnsPhone || 2;
                                    break;
                                case ScreenSize.Tablet:
                                    columnCount = props.blockConfig.block.columnsTablet || 3;
                                    break;
                            }
                        }
                        const cellContentConfig = Defaults.cellDesign(props.config, props.blockConfig, Kit.Context.Cell.Exhibition.Grid);
                        const columnSize = Layout.columnSize(columnCount);
                        content = props.data.shows.map((d, i) =>
                            <Column className="col--custom" key={d.showId} size={columnSize}>
                                <ShowListCell.ShowCellContainer
                                    {...props}
                                    animation={animation}
                                    columnCount={columnCount}
                                    columnSize={columnSize}
                                    contentConfig={cellContentConfig}
                                    data={d}
                                    index={i}
                                    listData={props.data.shows} />
                            </Column>
                        );
                        className = 'cont--kit__content cont__row--custom';
                    } else {
                        content = (
                            <Spaced>
                                <Row>
                                    {props.data.shows.map((d) => <Cell key={d.showId} {...props} data={d} />)}
                                </Row>
                            </Spaced>
                        );
                    }
                }
                content = (
                    <ContentContainer className={className} style={props.style}>
                        {
                            !props.data &&
                            <LoadingView />
                        }
                        {
                            content != undefined &&
                            <React.Fragment>
                                {content}
                                <Footer
                                    active={props.active}
                                    builder={props.builder}
                                    hasMore={props.data.hasMore}
                                    loading={props.loading}
                                    onLoadMore={props.onLoadMore} />
                            </React.Fragment>
                        }
                    </ContentContainer>
                );
                if (props.containerStyle) {
                    content = <div className="cont--kit__wrapper" style={props.containerStyle}>{content}</div>
                }
                break;
        }
        return (
            <React.Fragment>
                {content}
                <JsonLD
                    baseUrl={this.props.baseUrl}
                    builder={this.props.builder}
                    jsonLD={this.props.jsonLD}
                    path="show"
                    querySub={this.props.querySub} />
            </React.Fragment>
        );
    }
}
const ShowsListComponent =
    withConfig(
        withPaging(
            withGridConfig(
                _ShowsListComponent,
                defaultColumnCount
            ),
            { limit: 100, path: 'shows', resultKey: 'shows' }));

// region GRID

const ShowsListGrid = { Component: ShowsListComponent };

function ContentContainer(props) {
    return (
        <_ContentContainer className={props.className} style={props.style} type="shows">
            {props.children}
        </_ContentContainer>
    );
}

function Cell(props) {
    let imageTuples;
    if (props.data.image) {
        imageTuples = Image.sizes.map(function (d) {
            return { size: d, url: Image.url(props.data.image, d, d, 'c_fill') };
        });
    }
    const link = Url.getLink('', `show/${props.data.showId}`, { sub: props.querySub, layout: props.queryLayout, dataSub: props.queryDataSub }, props.builder);
    return (
        <Column size={props.columnSize} style={props.builder ? { pointerEvents: 'none' } : undefined}>
            <a href={link}>
                <div className="show__card">
                    {
                        imageTuples &&
                        <div className="show__card__img">
                            <ImageSrcSet
                                builder={props.builder}
                                config={props.config}
                                data={{ alt: props.data.title, title: props.data.title }}
                                imageTuples={imageTuples} />
                        </div>
                    }
                    <div className="show__card__inn">
                        <div className="show__card__title">
                            {props.data.title}
                        </div>
                        <div className="show__card__details">
                            <span className="show__card__text show__card__date">{props.data.date}</span>
                        </div>
                    </div>
                </div>
            </a>
        </Column>
    );
}

// region PARALLAX

function ShowsListParallaxComponent(props) {
    const _props = {};
    Utilities.copyObject(props, _props);
    if (!_props.context) {
        _props.context = {};
    }
    _props.context.displayType = 'parallax';
    return <ShowsListComponent {..._props} />
}
const ShowsListParallax = { Component: ShowsListParallaxComponent };

// region SLIDESHOW

function _Slideshow(props) {
    const blockType = props.blockType || Name.Block.Show.List.Slideshow;
    let contentConfig = Defaults.Block[blockType].Cell;
    if (props.blockConfig.slideshow && props.blockConfig.slideshow.cell && props.blockConfig.slideshow.cell.desktop && props.blockConfig.slideshow.cell.desktop.blocks && props.blockConfig.slideshow.cell.desktop.blocks.length > 0) {
        contentConfig = props.blockConfig.slideshow.cell;
    }
    const style = {};
    Utilities.copyObject(props.style, style);
    const wrapperStyle = {}
    if (style.marginLeft) {
        wrapperStyle.marginLeft = style.marginLeft;
        style.marginLeft = undefined;
    }
    if (style.marginRight) {
        wrapperStyle.marginRight = style.marginRight;
        style.marginRight = undefined;
    }
    if (style.paddingLeft) {
        wrapperStyle.paddingLeft = style.paddingLeft;
        style.paddingLeft = undefined;
    }
    if (style.paddingRight) {
        wrapperStyle.paddingRight = style.paddingRight;
        style.paddingRight = undefined;
    }
    const handleContent = (content, style) => <div className="slide__content" style={style}>{content}</div>
    const listData = props.listData || [];
    return (
        <div style={{ position: 'relative' }}>
            <_ContentContainer className="cont--kit__wrapper" style={props.containerStyle}>
                <Spaced style={wrapperStyle}>
                    <Row className="cont--shows slider--whitelabel" id={props.id} style={style}>
                        <div id={props.sliderId} className="slider slider--full__width slider--fit slider--shows" {...props.sliderProps}>
                            {
                                listData.map((d, i) =>
                                    <ShowListCell.ShowCellContainer
                                        {...props}
                                        key={`${d.id}-${i}`}
                                        contentConfig={contentConfig}
                                        data={d}
                                        type="slideshow"
                                        updateBackgroundColor={false}
                                        onContent={handleContent} />
                                )
                            }
                        </div>
                        {props.arrows}
                    </Row>
                </Spaced>
            </_ContentContainer>
        </div>
    );
}
const Slideshow = withSlideshowInitialization(
    withSlideshowConfiguration(_Slideshow),
    {
        autoplay: false,
        dots: false,
        items: 5,
        loop: true,
        nav: true,
        navText: false,
        margin: 40,
        responsive: {
            0: {
                items: 1
            },
            600: {
                items: 3
            },
            1000: {
                items: 5
            }
        },
        smartSpeed: 1000
    }
);

function ShowsListSlideshowComponent(props) {
    const _props = {};
    Utilities.copyObject(props, _props);
    if (!_props.context) {
        _props.context = {};
    }
    _props.context.displayType = 'slideshow';
    return <ShowsListComponent {..._props} />
}
const ShowsListSlideshow = { Component: ShowsListSlideshowComponent };

export { ShowsListGrid, ShowsListParallax, ShowsListSlideshow };